import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
// import { useDispatch } from 'react-redux';
import { Box, Button, FooterActionBar } from '@saviynt/design-system';

import testIds from '../../../../constants/testIds';
import useLocalization from '../../../../hooks/use-localize';
import Logger from '../../../../services/loggerService';
import {
  type ConnectionPayload,
  type FormInputFields,
} from '../../data-layer/ApplicationCard';
import { useDiscoverySaveConnectionMutation } from '../../data-layer/ApplicationSlice';
import { resetDiscoverySaveConnectionState } from '../../data-layer/DiscoverySaveConnectionSlice';

import './ApplicationCardFooter.css';

interface ApplicationCardFooterProps {
  connectionJson: string;
  allFields: FormInputFields[];
  connectionType: string;
  appTypeKey: string;
  createNewConnection: boolean;
  backToExistingConnection: (value: boolean) => void;
}

/**
 * Application Card Footer Component
 */
function ApplicationCardFooter({
  appTypeKey,
  connectionJson,
  allFields,
  connectionType,
  createNewConnection,
  backToExistingConnection,
}: ApplicationCardFooterProps) {
  const dispatch = useDispatch();
  const formatMessage = useLocalization();
  const [successTestFields, setSuccessTestFields] = useState<FormInputFields[]>(
    []
  );
  const [disableSave, setDisableSave] = useState(true);
  const [, setSaveDisable] = useState(true);

  const [discoverySaveConnection, { isLoading }] =
    useDiscoverySaveConnectionMutation();

  const handleFieldsValueChanged = (
    currentFields: FormInputFields[],
    testedFields: FormInputFields[]
  ) => {
    if (
      testedFields.length === 0 ||
      currentFields.length !== testedFields.length
    ) {
      setSaveDisable(true);

      return;
    }

    const regex = /@[a-zA-Z0-9_]+@/;

    const isChanged = currentFields.some((field, index) => {
      const tested = testedFields[index];

      return (
        field.attributeName &&
        regex.test(field.attributeName) &&
        field.value !== tested.value
      );
    });

    setSaveDisable(isChanged);
  };

  useEffect(() => {
    if (allFields.length > 0) {
      const hasAnyError = allFields.some((field) => field.hasError);
      const hasEmptyField = allFields.some(
        (field) =>
          field.value === null ||
          field.value === undefined ||
          field.value === ''
      );

      if (hasEmptyField) {
        setDisableSave(true);
      } else if (!hasEmptyField && hasAnyError) {
        setDisableSave(true);
      } else if (!hasEmptyField && !hasAnyError) {
        setDisableSave(false);
      }

      handleFieldsValueChanged(allFields, successTestFields);
    }

    return () => {
      setDisableSave(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFields]);

  const handleTestConnection = async () => {
    const testPayload: ConnectionPayload = {
      appTypeKey,
      categoryName: 'Connection',
      ConnectionJSON: connectionJson,
      connectiontype: connectionType,
    };

    allFields.forEach((field) => {
      testPayload[field.payloadAtrributeName] = field.value;
    });
    dispatch(resetDiscoverySaveConnectionState());

    try {
      const response = await discoverySaveConnection(testPayload).unwrap();

      if (response.connectionStatus === 'SUCCESS') {
        setSuccessTestFields(allFields);
      } else {
        setDisableSave(true);
        setSuccessTestFields([]);
      }
    } catch (error) {
      Logger.error('Error generating form template:', error);
    }
  };

  const handleBackToExistingConnection = () => {
    backToExistingConnection(false);
  };

  return (
    <FooterActionBar className='ApplicationCard' size='large'>
      <Box className='ApplicationCard-FooterActionBar'>
        <Box
          className={
            createNewConnection
              ? 'ApplicationCard-FooterActionBar-Back'
              : 'ApplicationCard-FooterActionBar-Buttons'
          }>
          {createNewConnection && (
            <Button
              dataTestId={
                testIds.DISCOVERY_APPLICATIONCARD_CREATE_CONNECTION_BACK_BUTTON
              }
              kind='outlined'
              onClick={handleBackToExistingConnection}>
              {formatMessage('INTEGRATIONS.DISCOVERY.BACK', 'Back')}
            </Button>
          )}
          <Button
            dataTestId={
              testIds.DISCOVERY_APPLICATIONCARD_CREATE_CONNECTION_SAVE_BUTTON
            }
            kind='filled'
            isDisabled={disableSave}
            size='medium'
            loaderText={formatMessage('INTEGRATIONS.BUTTON.SAVING', 'Saving')}
            isLoading={isLoading}
            onClick={handleTestConnection}>
            {formatMessage('INTEGRATIONS.BUTTON.SAVE', 'Save')}
          </Button>
        </Box>
      </Box>
    </FooterActionBar>
  );
}

export default ApplicationCardFooter;
